import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button, message } from 'antd'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import OllieNoticedRows from 'src/components/ollie/OllieNoticedRows'
import { OLLIE_NOTICED_PAGES } from 'src/utils/ollieNoticedRows'
import styled from 'styled-components/macro'
import { BodyText, H1, H3 } from 'src/branding/typography'
import FoodSafetyDayNav from 'src/components/food-safety/FoodSafetyDayNav'
import { useOllieAssistant } from 'src/components/ollie/OllieAssistantContext'
import FadeIn from 'src/components/styled/FadeIn'
import Loading from 'src/components/styled/Loading'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import {
  FOOD_SAFETY_HISTORY_LOCKED_CODE,
  FOOD_SAFETY_HOME_INTRO,
  FOOD_SAFETY_HOME_NO_FRIDGES_LINE,
  FOOD_SAFETY_HOME_STATUS,
  FOOD_SAFETY_OLLIE_COACH_DRAFT,
  FOOD_SAFETY_OLLIE_COACH_LABEL,
  FOOD_SAFETY_REPORTS_PAYWALL_REASON,
  FOOD_SAFETY_SECONDARY_TEXT,
  FOOD_SAFETY_SECTION_LABELS,
  FOOD_SAFETY_SURFACE,
} from 'src/constants/foodSafety'
import useIsBlocked, { useCanAccess } from 'src/hooks/useIsBlocked'
import useFoodSafetyLatestRequest from 'src/hooks/useFoodSafetyLatestRequest'
import {
  FOOD_SAFETY_CHANGED_EVENT,
  FOOD_SAFETY_HOME_POLLED_EVENT,
} from 'src/hooks/useFoodSafetyTodayBadgeCount'
import FoodSafetyReportsPaywallModal from 'src/screens/food-safety/FoodSafetyReportsPaywallModal'
import { fetchFoodSafetyHome } from 'src/utils/foodSafetyApi'
import {
  trackFoodSafetyOllieOpened,
  trackFoodSafetyOpened,
} from 'src/utils/foodSafetyAnalytics'
import {
  buildFoodSafetyBrowseWindow,
  clampFoodSafetyBrowseDate,
  foodSafetyHomeSummaryTone,
  formatFoodSafetyHomeStatus,
  formatFoodSafetyHomeSummary,
  formatFoodSafetySectionDetail,
  formatFoodSafetySectionModeTag,
  formatFoodSafetySectionUnresolved,
  formatFoodSafetyViewingDayCue,
  foodSafetySectionHref,
  FOOD_SAFETY_BACK_TO_TODAY_LABEL,
  isFoodSafetyHomeFirstRun,
  foodSafetyHomeHasNoFridges,
  isFoodSafetyViewingToday,
  listFoodSafetyHomeUnresolved,
  applyFoodSafetySectionLayout,
  mergeFoodSafetyHomeSections,
  resolveFoodSafetySectionLogMode,
  ollieRowCoversFoodSafetyPrimary,
  pickFoodSafetyHomePrimaryAction,
  withFoodSafetyHomeNotDueYet,
  withoutFoodSafetyStillNeeded,
  resolveFoodSafetyHomeDateParam,
  shouldShowFoodSafetyBackToToday,
} from 'src/utils/foodSafetyHome'
import {
  FOOD_SAFETY_NEEDS_ACTION_PARAM,
  formatFoodSafetyUnresolvedTitle,
} from 'src/utils/foodSafetyCorrectiveAction'
import {
  fetchFoodSafetySectionLayout,
  restoreFoodSafetySectionLayout,
  saveFoodSafetySectionLayout,
} from 'src/utils/venueHomeApi'

const RECORD_TILES = [
  {
    section: 'documents',
    kind: 'documents',
    label: 'Documents',
    path: '/food-safety/documents',
    testId: 'food-safety-section-documents',
    status: 'Upload and store',
    detail: 'Pest control, certificates, training',
  },
  {
    section: 'training',
    kind: 'training',
    label: 'Staff Training',
    path: '/food-safety/training',
    testId: 'food-safety-section-training',
    status: 'Employee training records',
    detail: 'Training, certificates and refresher dates',
  },
  {
    section: 'visitors',
    kind: 'visitors',
    label: 'Contractors & Visitors',
    path: '/food-safety/visitors',
    testId: 'food-safety-section-visitors',
    status: 'Sign in and review visits',
    detail: 'Attendance, declarations and departures',
  },
]

const SortableFsTile = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.7 : 1,
        cursor: 'grab',
        width: '100%',
        height: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  )
}

const FoodSafetyHome = () => {
  const blocked = useIsBlocked('food-safety')
  const canManage = useCanAccess('food-safety-manage')
  const { openOllieWithDraft } = useOllieAssistant()
  const history = useHistory()
  const location = useLocation()
  const { venueId: routeVenueId } = useParams()
  const venue = useSelector(state => state.venue?.venue)
  const venueId = String(venue?._id || routeVenueId || '')

  const queryDate = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('date') || undefined
  }, [location.search])

  const [localDate, setLocalDate] = useState(null)
  const [home, setHome] = useState(null)
  const [loading, setLoading] = useState(true)
  const [lockInfo, setLockInfo] = useState(null)
  const [historyPaywallOpen, setHistoryPaywallOpen] = useState(false)
  const latestHome = useFoodSafetyLatestRequest()
  // Day the last load showed, so clearing ?date=<today> doesn't load it again.
  const shownDayRef = useRef(null)

  const historyAccess = home?.historyAccess || lockInfo
  const browseWindow = useMemo(
    () =>
      buildFoodSafetyBrowseWindow(historyAccess, home?.localDate || localDate),
    [historyAccess, home?.localDate, localDate],
  )

  const syncHomeDateUrl = useCallback(
    (date, todayLocalDate) => {
      if (!venueId || !date) return
      if (location.pathname !== `/v/${venueId}/food-safety`) return
      // 29.72 — pin ?date= only when browsing away from today
      const pinned = resolveFoodSafetyHomeDateParam(date, todayLocalDate)
      const nextSearch = pinned ? `?date=${pinned}` : ''
      if (location.search === nextSearch) return
      history.replace(`/v/${venueId}/food-safety${nextSearch}`)
    },
    [history, location.pathname, location.search, venueId],
  )

  const openHistoryPaywall = useCallback(() => {
    setHistoryPaywallOpen(true)
  }, [])

  const loadHome = useCallback(
    async date => {
      if (!venueId) return
      const isCurrent = latestHome.begin()
      setLoading(true)
      setLockInfo(null)
      try {
        const data = withoutFoodSafetyStillNeeded(
          await fetchFoodSafetyHome(venueId, {
            date: date || undefined,
          }),
        )
        if (!isCurrent()) return
        shownDayRef.current = {
          venueId,
          localDate: data.localDate,
          isToday: Boolean(
            data.localDate && data.localDate === data.todayLocalDate,
          ),
        }
        setHome(data)
        setLocalDate(data.localDate)
        // Keep URL honest: clear ?date= on today; pin only for other days (29.72).
        if (data.localDate) {
          syncHomeDateUrl(data.localDate, data.todayLocalDate)
        }
      } catch (error) {
        if (!isCurrent()) return
        if (error?.code === FOOD_SAFETY_HISTORY_LOCKED_CODE) {
          setLockInfo(error.historyAccess)
          setHistoryPaywallOpen(true)
          const earliest = error.historyAccess?.window?.earliestLocalDate
          if (earliest && earliest !== date) {
            syncHomeDateUrl(
              earliest,
              error.historyAccess?.window?.latestLocalDate,
            )
          }
        } else {
          console.error(error)
          setHome(null)
          message.error(
            error?.response?.data?.message ||
              error?.message ||
              'Could not load Food Safety for this day.',
          )
        }
      } finally {
        isCurrent.done()
        if (isCurrent()) setLoading(false)
      }
    },
    [venueId, syncHomeDateUrl, latestHome],
  )

  useEffect(() => {
    if (!venueId) return undefined
    const shown = shownDayRef.current
    if (
      shown?.venueId === venueId &&
      (queryDate ? shown.localDate === queryDate : shown.isToday)
    ) {
      return undefined
    }
    trackFoodSafetyOpened({ venueId, surface: 'home' })
    loadHome(queryDate)
    return undefined
  }, [venueId, queryDate, loadHome])

  /** Quiet reload in place — no spinner; a failure keeps what's shown. */
  const refreshHome = useCallback(async () => {
    if (!venueId) return
    const isCurrent = latestHome.beginQuiet()
    if (isCurrent.skipped) return
    try {
      const data = withoutFoodSafetyStillNeeded(
        await fetchFoodSafetyHome(venueId, {
          date: queryDate || undefined,
        }),
      )
      if (!isCurrent()) return
      setHome(data)
      setLocalDate(data.localDate)
    } catch (error) {
      if (isCurrent()) console.error('Food Safety Home refresh failed', error)
    }
  }, [venueId, queryDate, latestHome])

  // Matches the nav badge: refresh after a Food Safety change and on coming
  // back to the tab; while today is on screen, take the badge's poll result
  // (so a wall tablet never shows a different count from the badge, and
  // Express sees one poll, not two).
  const homeShowsToday = !queryDate
  useEffect(() => {
    if (!venueId || typeof window === 'undefined') return undefined
    const visible = () =>
      typeof document === 'undefined' || document.visibilityState !== 'hidden'
    const onVisible = () => {
      if (visible()) refreshHome()
    }
    const onPolled = event => {
      const { venueId: polledVenue, home: polled } = event?.detail || {}
      if (!polled || String(polledVenue) !== String(venueId)) return
      const isCurrent = latestHome.beginQuiet()
      if (isCurrent.skipped || !isCurrent()) return
      const data = withoutFoodSafetyStillNeeded(polled)
      setHome(data)
      setLocalDate(data.localDate)
    }
    window.addEventListener(FOOD_SAFETY_CHANGED_EVENT, refreshHome)
    window.addEventListener('focus', onVisible)
    document.addEventListener('visibilitychange', onVisible)
    if (homeShowsToday) {
      window.addEventListener(FOOD_SAFETY_HOME_POLLED_EVENT, onPolled)
    }
    return () => {
      window.removeEventListener(FOOD_SAFETY_CHANGED_EVENT, refreshHome)
      window.removeEventListener('focus', onVisible)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener(FOOD_SAFETY_HOME_POLLED_EVENT, onPolled)
    }
  }, [venueId, refreshHome, homeShowsToday, latestHome])

  const sections = useMemo(
    () => mergeFoodSafetyHomeSections(home?.sections),
    [home?.sections],
  )
  const [sectionIds, setSectionIds] = useState(null)
  const [hiddenIds, setHiddenIds] = useState([])
  const [draftIds, setDraftIds] = useState([])
  const [draftHidden, setDraftHidden] = useState([])
  const [arranging, setArranging] = useState(false)
  const [layoutError, setLayoutError] = useState('')
  const [savingLayout, setSavingLayout] = useState(false)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  )
  useEffect(() => {
    if (!venueId) return undefined
    let cancelled = false
    fetchFoodSafetySectionLayout({ venueId })
      .then(layout => {
        if (cancelled) return null
        setSectionIds(layout.sectionIds)
        setHiddenIds(layout.hiddenIds || [])
        return null
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [venueId])
  const catalog = useMemo(() => {
    const rows = sections.map(section => ({
      section: section.section,
      row: section,
      kind: 'section',
    }))
    rows.push({ section: 'reports', kind: 'reports' })
    rows.push(...RECORD_TILES)
    return rows
  }, [sections])
  const tiles = useMemo(
    () =>
      applyFoodSafetySectionLayout(
        catalog,
        arranging ? draftIds : sectionIds,
        arranging ? draftHidden : hiddenIds,
      ),
    [catalog, arranging, draftIds, draftHidden, sectionIds, hiddenIds],
  )
  const tileLabel = tile =>
    tile.label ||
    tile.row?.label ||
    FOOD_SAFETY_SECTION_LABELS[tile.section] ||
    tile.section
  const beginArrange = () => {
    setLayoutError('')
    setDraftIds(tiles.map(tile => tile.section))
    setDraftHidden(hiddenIds)
    setArranging(true)
  }
  const saveArrange = async () => {
    setSavingLayout(true)
    setLayoutError('')
    try {
      const saved = await saveFoodSafetySectionLayout({
        venueId,
        sectionIds: draftIds,
        hiddenIds: draftHidden,
      })
      setSectionIds(saved.sectionIds)
      setHiddenIds(saved.hiddenIds || [])
      setArranging(false)
    } catch (error) {
      setLayoutError(error?.message || 'Could not save the layout.')
    } finally {
      setSavingLayout(false)
    }
  }
  const restoreArrange = async () => {
    setSavingLayout(true)
    setLayoutError('')
    try {
      await restoreFoodSafetySectionLayout({ venueId })
      setSectionIds(null)
      setHiddenIds([])
      setArranging(false)
    } catch (error) {
      setLayoutError(error?.message || 'Could not restore Food Safety.')
    } finally {
      setSavingLayout(false)
    }
  }
  const unresolved = useMemo(
    () => listFoodSafetyHomeUnresolved(sections),
    [sections],
  )
  const unresolvedTotal = unresolved.reduce((sum, row) => sum + row.count, 0)
  const primaryAction = useMemo(
    () => pickFoodSafetyHomePrimaryAction(home?.sections),
    [home?.sections],
  )
  const [ollieRows, setOllieRows] = useState([])
  const showPrimaryAction =
    Boolean(primaryAction) &&
    !ollieRowCoversFoodSafetyPrimary(primaryAction, ollieRows)

  const totals = withFoodSafetyHomeNotDueYet(home?.totals, home?.sections)
  const firstRun = canManage && isFoodSafetyHomeFirstRun(totals)
  const noFridges = foodSafetyHomeHasNoFridges(sections)
  const viewingToday = isFoodSafetyViewingToday(
    home?.localDate || localDate,
    home?.todayLocalDate,
  )
  const summaryTone = foodSafetyHomeSummaryTone(totals)
  const viewingDayCue = formatFoodSafetyViewingDayCue(
    home?.localDate || localDate,
    home?.todayLocalDate,
  )
  const showBackToToday = shouldShowFoodSafetyBackToToday(
    home?.localDate || localDate,
    home?.todayLocalDate,
  )

  const goToDate = nextDate => {
    const clamped = clampFoodSafetyBrowseDate(nextDate, browseWindow)
    if (!clamped || !venueId) return
    const today = home?.todayLocalDate
    // 29.72 — navigating to today clears the date param
    if (today && clamped === today) {
      if (queryDate) {
        history.push(`/v/${venueId}/food-safety`)
      } else {
        loadHome(undefined)
      }
      return
    }
    if (clamped === queryDate) return
    history.push(`/v/${venueId}/food-safety?date=${clamped}`)
  }

  const goToToday = () => {
    if (!venueId) return
    if (queryDate) {
      history.push(`/v/${venueId}/food-safety`)
    } else {
      loadHome(undefined)
    }
  }

  if (blocked) return <PermissionBlockScreen />

  if (!venueId) {
    return <Loading />
  }

  if (loading && !home) {
    return <Loading />
  }

  if (!home) {
    return (
      <Wrapper>
        <H1>Food Safety</H1>
        <BodyText>
          Couldn’t load today’s Food Safety status. Check you’re online, then
          retry.
        </BodyText>
        <Button type='primary' onClick={() => loadHome(localDate || undefined)}>
          Retry
        </Button>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <HeroBand>
        <HeaderRow>
          <HeroCopy>
            <PageTitle>Food Safety</PageTitle>
            <HeroIntro
              data-testid={
                noFridges
                  ? 'food-safety-home-no-fridges'
                  : 'food-safety-home-intro'
              }
            >
              {noFridges
                ? FOOD_SAFETY_HOME_NO_FRIDGES_LINE
                : FOOD_SAFETY_HOME_INTRO}
            </HeroIntro>
          </HeroCopy>
          <FoodSafetyDayNav
            localDate={localDate}
            browseWindow={browseWindow}
            onChangeDate={goToDate}
            onBrowseLocked={openHistoryPaywall}
            disabled={loading}
          />
        </HeaderRow>

        {viewingDayCue || showBackToToday ? (
          <PastDayCue data-testid='food-safety-past-day-cue'>
            {viewingDayCue ? (
              <PastDayCueText>{viewingDayCue}</PastDayCueText>
            ) : (
              <span />
            )}
            {showBackToToday ? (
              <BackToTodayButton
                type='button'
                data-testid='food-safety-back-to-today'
                onClick={goToToday}
                disabled={loading || !home?.todayLocalDate}
              >
                {FOOD_SAFETY_BACK_TO_TODAY_LABEL}
              </BackToTodayButton>
            ) : null}
          </PastDayCue>
        ) : null}

        <ActionRow>
          <ManageSetupButton
            to={`/v/${venueId}/food-safety/reports`}
            data-chrome='quiet'
            data-testid='food-safety-reports-link'
          >
            View EHO reports
          </ManageSetupButton>
          <ManageSetupButton
            to={`/v/${venueId}/food-safety/training`}
            data-chrome='quiet'
            data-testid='food-safety-training-link'
          >
            Staff Training
          </ManageSetupButton>
          {canManage ? (
            <ManageSetupButton
              to={`/v/${venueId}/food-safety/setup`}
              data-testid='food-safety-manage-link'
              data-first-run={firstRun ? 'true' : 'false'}
              data-chrome={firstRun ? undefined : 'quiet'}
            >
              {noFridges || firstRun ? 'Set up Food Safety' : 'Manage setup'}
            </ManageSetupButton>
          ) : (
            <ActionRowSpacer />
          )}
          <ManageSetupButton
            as='button'
            type='button'
            data-chrome='quiet'
            data-testid='food-safety-customise'
            onClick={() => (arranging ? saveArrange() : beginArrange())}
            disabled={savingLayout}
          >
            {arranging ? 'Save layout' : 'Customise'}
          </ManageSetupButton>
          <AskOllieButton
            type='button'
            data-testid='food-safety-ask-ollie'
            onClick={() => {
              trackFoodSafetyOllieOpened({ venueId, surface: 'home' })
              openOllieWithDraft(FOOD_SAFETY_OLLIE_COACH_DRAFT)
            }}
          >
            {FOOD_SAFETY_OLLIE_COACH_LABEL}
          </AskOllieButton>
        </ActionRow>

        {totals ? (
          <StatusBanner
            data-testid='food-safety-home-summary'
            data-tone={summaryTone}
          >
            <StatusMark aria-hidden data-tone={summaryTone} />
            <StatusBannerText>
              {formatFoodSafetyHomeSummary(totals, {
                viewingToday,
                closed: Boolean(home?.closed),
              })}
            </StatusBannerText>
          </StatusBanner>
        ) : null}
      </HeroBand>

      <OllieNoticedRows
        page={OLLIE_NOTICED_PAGES.FOOD_SAFETY}
        onRowsChange={setOllieRows}
      />

      {showPrimaryAction ? (
        <PrimaryCta
          type='button'
          data-testid='food-safety-home-primary-cta'
          onClick={() =>
            history.push(
              foodSafetySectionHref(venueId, primaryAction.section, {
                localDate,
                todayLocalDate: home?.todayLocalDate,
              }),
            )
          }
        >
          {primaryAction.ctaLabel}
        </PrimaryCta>
      ) : null}

      {unresolvedTotal > 0 ? (
        <UnresolvedStrip
          role='region'
          aria-label='Unresolved items'
          data-testid='food-safety-home-unresolved'
        >
          <UnresolvedText>
            <UnresolvedTitle>
              {formatFoodSafetyUnresolvedTitle(unresolvedTotal)}
            </UnresolvedTitle>
            <UnresolvedSub>
              Open a section to record corrective action.
            </UnresolvedSub>
          </UnresolvedText>
          <UnresolvedLinks>
            {unresolved.map(row => (
              <UnresolvedLink
                key={row.section}
                to={`/v/${venueId}/food-safety/section/${row.section}?${FOOD_SAFETY_NEEDS_ACTION_PARAM}=1`}
                data-testid={`food-safety-home-unresolved-${row.section}`}
              >
                {row.label} · {row.count}
              </UnresolvedLink>
            ))}
          </UnresolvedLinks>
        </UnresolvedStrip>
      ) : null}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={({ active, over }) => {
          if (!arranging || !over || active.id === over.id) return
          setDraftIds(current => {
            const from = current.indexOf(String(active.id))
            const to = current.indexOf(String(over.id))
            if (from < 0 || to < 0) return current
            return arrayMove(current, from, to)
          })
        }}
      >
        <SortableContext
          items={tiles.map(tile => tile.section)}
          strategy={rectSortingStrategy}
        >
          <SectionGrid>
            {tiles.map(tile => {
              let card = null
              if (tile.kind === 'section' && tile.row) {
                const section = tile.row
                const tone = section.status || FOOD_SAFETY_HOME_STATUS.IDLE
                const logMode = resolveFoodSafetySectionLogMode(section)
                const unresolvedLine =
                  formatFoodSafetySectionUnresolved(section)
                card = (
                  <SectionCard
                    type='button'
                    data-tone={tone}
                    data-log-mode={logMode}
                    data-arranging={arranging ? 'true' : undefined}
                    data-testid={`food-safety-section-${section.section}`}
                    onClick={() => {
                      if (arranging) return
                      history.push(
                        foodSafetySectionHref(venueId, section.section, {
                          localDate,
                          todayLocalDate: home?.todayLocalDate,
                        }),
                      )
                    }}
                  >
                    <ModeTag
                      data-log-mode={logMode}
                      data-testid={`food-safety-section-mode-${section.section}`}
                    >
                      {formatFoodSafetySectionModeTag(section)}
                    </ModeTag>
                    <CardTop>
                      <SectionTitle>
                        {section.label ||
                          FOOD_SAFETY_SECTION_LABELS[section.section] ||
                          section.section}
                      </SectionTitle>
                      <CardChevron aria-hidden>›</CardChevron>
                    </CardTop>
                    <StatusLabel data-tone={tone}>
                      {formatFoodSafetyHomeStatus(tone, section, {
                        viewingToday,
                        closed: Boolean(home?.closed),
                      })}
                    </StatusLabel>
                    <DetailText>
                      {formatFoodSafetySectionDetail({
                        ...section,
                        viewingToday,
                      })}
                    </DetailText>
                    {unresolvedLine ? (
                      <UnresolvedLine
                        data-testid={`food-safety-section-unresolved-${section.section}`}
                      >
                        {unresolvedLine}
                      </UnresolvedLine>
                    ) : null}
                    {arranging ? <DragHint>⋮⋮ Drag to move</DragHint> : null}
                  </SectionCard>
                )
              } else if (tile.kind === 'reports') {
                card = (
                  <SectionCard
                    as={arranging ? 'button' : Link}
                    type={arranging ? 'button' : undefined}
                    to={
                      arranging
                        ? undefined
                        : `/v/${venueId}/food-safety/reports`
                    }
                    data-tone='reports'
                    data-arranging={arranging ? 'true' : undefined}
                    data-testid='food-safety-section-reports'
                  >
                    <ModeTag>Overview</ModeTag>
                    <CardTop>
                      <SectionTitle>
                        {FOOD_SAFETY_SECTION_LABELS.reports}
                      </SectionTitle>
                      <CardChevron aria-hidden>›</CardChevron>
                    </CardTop>
                    <StatusLabel data-tone='reports'>
                      Recent &amp; historical records
                    </StatusLabel>
                    <DetailText>View records for an inspection</DetailText>
                    {arranging ? <DragHint>⋮⋮ Drag to move</DragHint> : null}
                  </SectionCard>
                )
              } else if (tile.path) {
                card = (
                  <SectionCard
                    as={arranging ? 'button' : Link}
                    type={arranging ? 'button' : undefined}
                    to={arranging ? undefined : `/v/${venueId}${tile.path}`}
                    data-tone='reports'
                    data-arranging={arranging ? 'true' : undefined}
                    data-testid={tile.testId}
                  >
                    <ModeTag>Kept safe</ModeTag>
                    <CardTop>
                      <SectionTitle>{tile.label}</SectionTitle>
                      <CardChevron aria-hidden>›</CardChevron>
                    </CardTop>
                    <StatusLabel data-tone='reports'>{tile.status}</StatusLabel>
                    <DetailText>{tile.detail}</DetailText>
                    {arranging ? <DragHint>⋮⋮ Drag to move</DragHint> : null}
                  </SectionCard>
                )
              }
              if (!card) return null
              const wrapped = arranging ? (
                <SortableFsTile key={tile.section} id={tile.section}>
                  {card}
                  <Button
                    type='link'
                    onPointerDown={event => event.stopPropagation()}
                    onClick={() => {
                      setDraftIds(current =>
                        current.filter(id => id !== tile.section),
                      )
                      setDraftHidden(current =>
                        current.includes(tile.section)
                          ? current
                          : [...current, tile.section],
                      )
                    }}
                  >
                    Hide
                  </Button>
                </SortableFsTile>
              ) : (
                <React.Fragment key={tile.section}>{card}</React.Fragment>
              )
              return wrapped
            })}
          </SectionGrid>
        </SortableContext>
        {arranging ? (
          <LayoutNote data-testid='food-safety-layout-note'>
            Drag the cards into the order you want, then Save layout. Hide takes
            a tile off this tab.
            {layoutError ? ` ${layoutError}` : ''}
            <LayoutActions>
              {catalog
                .filter(tile => !draftIds.includes(tile.section))
                .map(tile => (
                  <Button
                    key={tile.section}
                    onClick={() => {
                      setDraftHidden(current =>
                        current.filter(id => id !== tile.section),
                      )
                      setDraftIds(current => [...current, tile.section])
                    }}
                  >
                    Show {tileLabel(tile)}
                  </Button>
                ))}
              <Button
                onClick={() => setArranging(false)}
                disabled={savingLayout}
              >
                Cancel
              </Button>
              <Button onClick={restoreArrange} disabled={savingLayout}>
                Restore default
              </Button>
            </LayoutActions>
          </LayoutNote>
        ) : null}
      </DndContext>

      <FoodSafetyReportsPaywallModal
        visible={historyPaywallOpen}
        reason={FOOD_SAFETY_REPORTS_PAYWALL_REASON.OLDER}
        olderRecordCount={historyAccess?.olderRecordCount}
        upgradeHint={historyAccess?.upgradeHint}
        onClose={() => setHistoryPaywallOpen(false)}
      />
    </Wrapper>
  )
}

const Wrapper = styled(FadeIn)`
  width: 100%;
  max-width: 72rem;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.sm};
  padding-bottom: 5.5rem;
  box-sizing: border-box;
  /* Ollie is fixed 24px from the corner and 56px wide. When this column
     reaches that corner, keep every tile to the left of the button. */
  padding-right: max(
    ${({ theme }) => theme.spacing.sm},
    calc(6rem - max(0px, (100vw - 72rem) / 2))
  );

  ${({ theme }) => theme.device('md')`
    padding: ${theme.spacing.md};
    padding-bottom: 5.5rem;
    padding-right: max(
      ${theme.spacing.md},
      calc(6rem - max(0px, (100vw - 72rem) / 2))
    );
  `};
`

const HeroBand = styled.section`
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  border-radius: ${FOOD_SAFETY_SURFACE.CARD_RADIUS};
  background: ${FOOD_SAFETY_SURFACE.HERO_BG};
  box-shadow: ${FOOD_SAFETY_SURFACE.CARD_SHADOW};
  /* Thin lime accent — premium ops, not fireworks */
  border-top: 3px solid
    ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
`

const HeaderRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  min-width: 0;

  ${({ theme }) => theme.device('md')`
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  `};
`

const HeroCopy = styled.div`
  min-width: 0;
  flex: 1 1 auto;
`

const PageTitle = styled(H1)`
  margin: 0;
  font-size: 2rem;
  line-height: 1.15;
  color: ${({ theme }) => theme.colours.bbqBlack || '#000'};
  letter-spacing: -0.02em;

  ${({ theme }) => theme.device('md')`
    font-size: 2.25rem;
  `};
`

const HeroIntro = styled(BodyText)`
  display: block;
  margin: 0.4rem 0 0;
  max-width: 34rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  font-size: 0.95rem;
  line-height: 1.5;
`

const PastDayCue = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.xs};
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  padding: 0.55rem 0.75rem;
  border: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
`

const PastDayCueText = styled.span`
  font-size: 0.9rem;
  line-height: 1.35;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  font-weight: 500;
`

const BackToTodayButton = styled.button`
  appearance: none;
  margin: 0;
  padding: 0.35rem 0.75rem;
  border: 1.5px solid ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    background: ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`

const ManageSetupButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  padding: 0.5rem 1rem;
  border: 1.5px solid ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  font-weight: 600;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  line-height: 1.2;
  white-space: nowrap;
  text-decoration: none !important;
  transition: background 0.15s ease, border-color 0.15s ease;

  &[data-first-run='true'] {
    background: ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
    box-shadow: 0 1px 0 rgba(19, 45, 7, 0.08);
  }

  &[data-chrome='quiet'] {
    padding: 0.15rem 0;
    border: none;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    font-size: 0.85rem;
    font-weight: 600;
    color: ${FOOD_SAFETY_SECONDARY_TEXT};
    text-decoration: underline !important;
    text-underline-offset: 0.15rem;

    &:hover,
    &:focus {
      background: transparent;
      color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
      text-decoration: underline !important;
    }
  }

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
    color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
    text-decoration: none !important;
  }
`

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  min-width: 0;
  overflow: visible;
`

const ActionRowSpacer = styled.span`
  flex: 1 1 auto;
  min-width: 0;
`

const AskOllieButton = styled.button`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  max-width: 100%;
  box-sizing: border-box;
  margin-left: auto;
  padding: 0.5rem 1rem;
  border: 1.5px solid ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  font-weight: 600;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  line-height: 1.25;
  white-space: nowrap;
  overflow: visible;
  text-overflow: clip;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover,
  &:focus {
    background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
  }
`

const StatusBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin: 0;
  padding: 0.65rem 0.85rem;
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  border: 1px solid ${FOOD_SAFETY_SURFACE.LIME_EDGE};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};

  &[data-tone='attention'] {
    border-color: #e8c4b0;
    background: #fffaf7;
  }

  &[data-tone='all_done'] {
    border-color: ${FOOD_SAFETY_SURFACE.LIME_EDGE};
    background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
  }

  &[data-tone='idle'] {
    border-color: ${({ theme }) => theme.colours.panSilver || '#e0dfdf'};
    background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  }
`

const StatusMark = styled.span`
  flex: 0 0 auto;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: ${FOOD_SAFETY_SECONDARY_TEXT};

  &[data-tone='attention'],
  &[data-tone='in_progress'] {
    background: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
  }
  &[data-tone='all_done'] {
    background: ${FOOD_SAFETY_SURFACE.STATUS_DONE};
  }
  &[data-tone='ready'] {
    background: ${FOOD_SAFETY_SURFACE.STATUS_READY};
  }
  &[data-tone='idle'] {
    background: ${({ theme }) => theme.colours.oliveGrey || '#919191'};
  }
`

const StatusBannerText = styled.p`
  margin: 0;
  font-weight: 600;
  font-size: 0.95rem;
  line-height: 1.35;
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
`

const PrimaryCta = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  padding: 0.75rem 1rem;
  border: none;
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  background: ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  font-weight: 700;
  font-size: 0.95rem;
  line-height: 1.3;
  text-align: center;
  cursor: pointer;
  box-shadow: 0 1px 0 rgba(19, 45, 7, 0.08);

  &:hover,
  &:focus {
    filter: brightness(0.97);
  }
`

const DragHint = styled.span`
  display: block;
  margin-top: auto;
  padding-top: 0.65rem;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const LayoutNote = styled.p`
  margin: 0.75rem 0 0;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  font-size: 0.9rem;
`

const LayoutActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing.sm};

  /* Prefer 2-up sooner on laptop/narrow desktop (sidebar shrinks content). */
  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: 1100px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`

const SectionCard = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.3rem;
  width: 100%;
  box-sizing: border-box;
  min-height: 6.5rem;
  text-align: left;
  text-decoration: none;
  color: inherit;
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  border: 1px solid ${({ theme }) => theme.colours.panSilver || '#e0dfdf'};
  border-left: 3px solid ${({ theme }) => theme.colours.panSilver || '#e0dfdf'};
  border-radius: ${FOOD_SAFETY_SURFACE.CARD_RADIUS};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  box-shadow: ${FOOD_SAFETY_SURFACE.CARD_SHADOW};
  cursor: pointer;

  &[data-arranging='true'] {
    cursor: grab;
    min-height: 11rem;
  }

  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    background 0.15s ease;

  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
    border-left-color: ${({ theme }) =>
      theme.colours.lettuceGreen || '#b5d922'};
    background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
    box-shadow: ${FOOD_SAFETY_SURFACE.CARD_SHADOW_HOVER};
    outline: none;
  }

  &[data-tone='attention'],
  &[data-tone='in_progress'] {
    border-left-color: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
  }

  &[data-tone='all_done'] {
    border-left-color: ${FOOD_SAFETY_SURFACE.STATUS_DONE};
  }

  &[data-tone='ready'] {
    border-left-color: ${({ theme }) =>
      theme.colours.lettuceGreen || '#b5d922'};
  }

  &[data-tone='reports'] {
    border-left-color: ${({ theme }) =>
      theme.colours.seaweedGreen || '#132d07'};
  }

  /* Records made when something happens — dashed, so they don't read as due checks. */
  &[data-log-mode='event_driven'] {
    border-style: dashed;
    border-left-style: solid;
  }
`

const ModeTag = styled.span`
  align-self: flex-start;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};

  &[data-log-mode='scheduled'] {
    color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  }
`

const UnresolvedLine = styled.span`
  font-size: 0.82rem;
  font-weight: 600;
  color: #a35b00;
`

const UnresolvedStrip = styled.section`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 1rem;
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  padding: 0.65rem 1rem;
  border: 1px solid #f0d9b5;
  border-radius: ${FOOD_SAFETY_SURFACE.CARD_RADIUS};
  background: #fffaf2;
`

const UnresolvedText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`

const UnresolvedTitle = styled.strong`
  color: #8a4b00;
`

const UnresolvedSub = styled.span`
  font-size: 0.85rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const UnresolvedLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`

const UnresolvedLink = styled(Link)`
  padding: 0.2rem 0.65rem;
  border: 1px solid #e8c48a;
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  color: #8a4b00;
  font-size: 0.85rem;
  font-weight: 600;

  &:hover,
  &:focus {
    border-color: #c47a12;
    color: #8a4b00;
  }
`

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
`

const CardChevron = styled.span`
  flex: 0 0 auto;
  margin-top: -0.1rem;
  font-size: 1.35rem;
  line-height: 1;
  font-weight: 400;
  color: ${({ theme }) => theme.colours.oliveGrey || '#919191'};
`

const SectionTitle = styled(H3)`
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.3;
  letter-spacing: -0.01em;
`

const StatusLabel = styled.span`
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: none;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};

  &[data-tone='attention'],
  &[data-tone='in_progress'] {
    color: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
  }
  &[data-tone='all_done'] {
    color: ${FOOD_SAFETY_SURFACE.STATUS_DONE};
  }
  &[data-tone='ready'] {
    color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  }
  &[data-tone='reports'] {
    color: ${FOOD_SAFETY_SECONDARY_TEXT};
  }
`

const DetailText = styled(BodyText)`
  margin: 0.1rem 0 0;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  font-size: 0.9rem;
  line-height: 1.4;
`

export default FoodSafetyHome
